import React from 'react';

import useThemeColor from '@lib/hooks/useThemeColor';

import { Share as ShareIcon } from '@assets/svg/revamp';

import Button from '@components/Button';
import { type StackProps } from '@components/Stack';
import { SHARE_ENTRY_LABEL } from '@components/OrderShareSheet/copy';

export interface EntryButtonProps {
    onPress: VoidFunction;
    /** Defaults describe the order-detail sheet; the favorites drawer reuses it (JSAD-7305). */
    testID?: string;
    accessibilityLabel?: string;
    accessibilityHint?: string;
}

/**
 * Flat overrides (not nested under `sx`) because `useButtonVariant` spreads these straight
 * into the variant's own style object — a nested key would be inert.
 *
 * The variant does the styling: `primary` is white-on-navy-border with navy text, and the
 * `small` shape brings the pill radius plus the 2/4pt faux shadow (Android has no
 * box-shadow, so the thicker bottom/right border stands in for it). Only the sizing needs
 * correcting:
 *
 * `small` is a FIXED 141pt wide, and `center` in this codebase is
 * `justifyContent: 'space-around'`, not `center` (`useShorthandStyles.ts`) — so the spare
 * width was distributed *between* the icon and the label, shoving them to opposite ends and
 * making the layout's `gap: 8` irrelevant. Letting the button hug its content restores the
 * intended tight icon-to-label spacing. Height follows suit: the shape's 23pt cannot
 * contain a 16pt icon with padding.
 */
const ENTRY_SX: StackProps = {
    width: 'auto',
    height: 'auto',
    px: 16,
    py: 8
};

/**
 * The `Share` affordance itself, kept platform-free so the native and web containers
 * (which differ only in which `useShareItem` and which sheet shell they pull in) share
 * one definition of how it looks and reads.
 */
const EntryButton: React.FC<EntryButtonProps> = ({
    onPress,
    testID = 'order-share-button',
    accessibilityLabel = 'Share items from this order',
    accessibilityHint = "Opens a list of this order's items, each with a link you can share"
}) => {
    const iconColor = useThemeColor('primary.dark');

    return (
        <Button
            testID={testID}
            variant="primary.small"
            onPress={onPress}
            sx={ENTRY_SX}
            rowIcon
            icon={<ShareIcon width={16} height={16} color={iconColor} />}
            tx={{ variant: 'button.small', upcase: true }}
            accessibilityLabel={accessibilityLabel}
            accessibilityHint={accessibilityHint}
        >
            {SHARE_ENTRY_LABEL}
        </Button>
    );
};

export default EntryButton;
